/**
 * The rail's icons, drawn here rather than fetched.
 *
 * The prototype references a sprite at `assets/icons/icons.svg`. Pulling it
 * from that origin would make the product's own navigation depend on a
 * prototype's hosting, and inlining someone else's sprite wholesale imports
 * fifteen icons to use six. These are the six, keyed by the 9B2 screen id that
 * owns them.
 *
 * Every one is `aria-hidden`. The label beside it is the accessible name, so a
 * screen reader hears "Portfolio" once rather than an icon's guess at what it
 * depicts — and a screen with no icon for its id still has a working link.
 */
const PATHS: Record<string, React.ReactNode> = {
  // B1-02 Portfolio — buildings side by side.
  'B1-02': (
    <>
      <path d="M3 21V8l6-3v16" />
      <path d="M9 21V11l6-3v13" />
      <path d="M15 21V13l6-2.5V21" />
      <path d="M2 21h20" />
    </>
  ),
  // B3-17 My Work — a list with a check against it.
  'B3-17': (
    <>
      <path d="M4 7h10M4 12h10M4 17h6" />
      <path d="M16 16.5l2 2 4-4" />
    </>
  ),
  // B3-24 People.
  'B3-24': (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
      <path d="M16 5.5a3 3 0 0 1 0 5.6" />
      <path d="M17.5 14.6c2.1.6 3.5 2.3 3.5 4.4" />
    </>
  ),
  // B5-01 Search.
  'B5-01': (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" />
    </>
  ),
  // B1-03 Attention — the bell, as the prototype draws it.
  'B1-03': (
    <>
      <path d="M6 9a6 6 0 0 1 12 0c0 4 1.5 5.5 2 6H4c.5-.5 2-2 2-6Z" />
      <path d="M10 19a2 2 0 0 0 4 0" />
    </>
  ),
  // B1-05 Property — a building.
  'B1-05': (
    <>
      <path d="M5 21V5.5A1.5 1.5 0 0 1 6.5 4h7A1.5 1.5 0 0 1 15 5.5V21" />
      <path d="M15 10h3.5a1.5 1.5 0 0 1 1.5 1.5V21" />
      <path d="M3 21h18" />
      <path d="M8.5 8h3M8.5 11.5h3M8.5 15h3" />
    </>
  ),
  // B2-07 Evidence — a document.
  'B2-07': (
    <>
      <path d="M7 3.5h7l4.5 4.5v11a1.5 1.5 0 0 1-1.5 1.5H7A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5Z" />
      <path d="M14 3.5V8h4.5" />
      <path d="M8.5 12.5h7M8.5 16h5" />
    </>
  ),
  // B3-15 Actions — a box ticked.
  'B3-15': (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M8 12l2.5 2.5L16 9" />
    </>
  ),
  // B3-25 Notifications — an inbox tray; the bell is Attention's.
  'B3-25': (
    <>
      <path d="M4 13l2.2-7.1A1.5 1.5 0 0 1 7.6 5h8.8a1.5 1.5 0 0 1 1.4.9L20 13" />
      <path d="M4 13v5.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V13h-4.5l-1.5 2.5h-4L8.5 13Z" />
    </>
  ),
  // Sign out — leaving through a door.
  signout: (
    <>
      <path d="M14 4h4.5A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5H14" />
      <path d="M10 16l-4-4 4-4" />
      <path d="M6 12h9" />
    </>
  ),
  // B3-19 Inspections — a clipboard, which is what an inspector carries.
  'B3-19': (
    <>
      <path d="M9 4h6v3H9z" />
      <path d="M15 5.5h2.5A1.5 1.5 0 0 1 19 7v12.5A1.5 1.5 0 0 1 17.5 21h-11A1.5 1.5 0 0 1 5 19.5V7a1.5 1.5 0 0 1 1.5-1.5H9" />
      <path d="M8.5 12h7M8.5 16h4" />
    </>
  ),
};

export function RailIcon({ screen }: { screen: string }) {
  const drawn = PATHS[screen];
  return (
    <svg
      className="rail__icon"
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {/* A screen with no icon yet gets a neutral mark rather than nothing:
          an empty box in a rail of icons reads as a broken image. */}
      {drawn ?? <circle cx="12" cy="12" r="7.5" />}
    </svg>
  );
}
